/* eslint-disable no-use-before-define */
import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, TextArea } from 'uxd-design-system-components';
import { LocaleContext } from '../../contexts/index';
import getText from '../../assets/locale/locale';
import './CommentModal.scss';

const CommentModal = ({
  displayCommentModal,
  setDisplayCommentModal,
  setUserComments,
  contactName,
  sendResultsToFA,
  faInfo,
}) => {
  const locId = useContext(LocaleContext);
  /* eslint-disable-next-line no-useless-escape */
  const commentRegex = /[`~\*\^\[\]{}]/;
  const MAX_LENGTH = 500;
  const faFullName = `${faInfo.faFirstName} ${faInfo.faLastName}`;
  const defaultText = getText(locId, 'Results', 'defaultText', 'CommentModal');
  const faHeadshotImage = faInfo.faPhoto;
  const invalidComment = 
        `${getText(locId, 'Results', 'commentError', 'CommentModal'
  )} ${
  getText(locId, 'Results', 'invalidChars', 'CommentModal')}`;

  const [isShareButtonDisabled, setIsShareButtonDisabled] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const [subText, setSubText] = useState(
    getText(locId, 'Results', 'paragraphOneText', 'CommentModal').replace(
      '%MAX_LENGTH%',
      MAX_LENGTH
    )
  );
  const [charsLeft, setCharsLeft] = useState(MAX_LENGTH);
  const [localComment, setLocalComment] = useState('');

  const validateComment = (input) => {
    setLocalComment(input);
    setIsShareButtonDisabled(commentRegex.test(input));
    setCommentError(commentRegex.test(input) ? invalidComment : null);
  };

  const headerTextHandler = () => {
    const baseHeaderText = getText(locId, 'Results', 'header', 'CommentModal');
    let headerText;
    if (faInfo.faFirstName && faInfo.faLastName) {
      headerText = baseHeaderText
        .replace('%CONTACT_FIRSTNAME%', contactName.firstName)
        .replace('%FA_NAME%', faFullName);
    } else {
      headerText = baseHeaderText
        .replace('%CONTACT_FIRSTNAME%', contactName.firstName)
        .replace('%FA_NAME%', defaultText);
    }
    return headerText;
  };

  const shareButtonHandler = () => {
    setDisplayCommentModal(false);
    setUserComments(localComment);
    sendResultsToFA();
  };

  const commentHandler = (input) => {
    validateComment(input);
    setUserComments(input);
    setCharsLeft(MAX_LENGTH - input.length);
  };

  const buttons = [
    {
      text: getText(locId, 'Results', 'cancelButton', 'CommentModal'),
      onClick: () => setDisplayCommentModal(false),
      styleVariant: 'secondary',
    },
    {
      text: getText(locId, 'Results', 'shareButton', 'CommentModal'),
      onClick: () => shareButtonHandler(),
      styleVariant: 'primary',
      disabled: isShareButtonDisabled,
    },
  ];

  useEffect(() => {
    if (charsLeft === MAX_LENGTH) {
      setSubText(
        getText(locId, 'Results', 'subTextOne', 'CommentModal').replace(
          '%MAX_LENGTH%',
          MAX_LENGTH
        )
      );
    } else {
      setSubText(
        getText(locId, 'Results', 'subTextTwo', 'CommentModal').replace(
          '%CHARS_LEFT%',
          charsLeft
        )
      );
    }
  }, [charsLeft]);

  return (
    <Dialog
      title=""
      open={displayCommentModal}
      buttons={buttons}
      className="commentModal"
      onRequestClose={() => setDisplayCommentModal(false)}
    >
      {' '}
      <div className="title-container">
        <h3>{headerTextHandler()}</h3>
        {faHeadshotImage ? (
          <div className="fa-headshot">
            <img
              src={`data:image/png;base64,${faHeadshotImage}`}
              alt=""
              role="presentation"
              data-testid="fa-headshot"
            />
          </div>
        ) : null}
      </div>
      <div>
        <p className="comment-modal-paragraph">
          {faInfo.faFirstName
            ? getText(
                locId,
                'Results',
                'paragraphOneText',
                'CommentModal'
              ).replace('your financial advisor', faInfo.faFirstName)
            : getText(locId, 'Results', 'paragraphOneText', 'CommentModal')}
        </p>
        <p className="comment-modal-paragraph">
          {getText(
            locId,
            'Results',
            'paragraphTwoText',
            'CommentModal'
          ).replace('%FA_FIRSTNAME%', faInfo.faFirstName)}
        </p>
      </div>
      <TextArea
        id="user-comment"
        label={getText(locId, 'Results', 'label', 'CommentModal')} // static text used here in-leu of the "optional" prop to allow for lowercase text
        className="CommentModal"
        name="user-comment"
        value={localComment}
        maxLength={MAX_LENGTH}
        microcopy={subText}
        onChange={(text) => commentHandler(text)}
        onBlur={validateComment}
        error={commentError}
      />
    </Dialog>
  );
};

CommentModal.propTypes = {
  displayCommentModal: PropTypes.bool.isRequired,
  setDisplayCommentModal: PropTypes.func.isRequired,
  setUserComments: PropTypes.func.isRequired,
  contactName: PropTypes.objectOf(PropTypes.string).isRequired,
  sendResultsToFA: PropTypes.func.isRequired,
  faInfo: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default CommentModal;
